import { Button } from '@/components/ui/button';
import { formatCount } from '@/lib/format';

type OffsetPagerProps = {
  mode?: 'offset';
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (offset: number) => void;
  label?: string;
  disabled?: boolean;
};

type CursorPagerProps = {
  mode: 'cursor';
  pageSize: number;
  loadedCount: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  label?: string;
  disabled?: boolean;
};

export type TablePagerProps = OffsetPagerProps | CursorPagerProps;

/** Compact pager matching existing admin table chrome (not a heavy DataTable). */
export function TablePager(props: TablePagerProps) {
  if (props.mode === 'cursor') {
    const { pageSize, loadedCount, hasPrev, hasNext, onPrev, onNext, label, disabled } = props;
    if (!hasPrev && !hasNext && loadedCount === 0) return null;
    return (
      <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {label ||
            (loadedCount
              ? `Showing ${formatCount(loadedCount)} (up to ${formatCount(pageSize)} per page)`
              : 'No rows')}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" disabled={disabled || !hasPrev} onClick={onPrev}>
            Previous
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={disabled || !hasNext} onClick={onNext}>
            Next
          </Button>
        </div>
      </div>
    );
  }

  const { total, limit, offset, onOffsetChange, label, disabled } = props;
  const safeLimit = Math.max(limit, 1);
  const pageCount = Math.max(1, Math.ceil(Math.max(total, 0) / safeLimit));
  const page = Math.min(Math.floor(offset / safeLimit), pageCount - 1);
  const from = total === 0 ? 0 : page * safeLimit + 1;
  const to = Math.min(total, (page + 1) * safeLimit);

  if (total <= safeLimit && page === 0) {
    return total > 0 ? (
      <div className="border-t border-border px-4 py-3">
        <p className="text-xs text-muted-foreground">
          {label || `${formatCount(total)} item${total === 1 ? '' : 's'}`}
        </p>
      </div>
    ) : null;
  }

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
      <p className="text-xs text-muted-foreground">
        {label ||
          (total === 0
            ? 'No items'
            : `${formatCount(from)}–${formatCount(to)} of ${formatCount(total)}`)}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || page <= 0}
          onClick={() => onOffsetChange(Math.max(0, (page - 1) * safeLimit))}
        >
          Previous
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || page >= pageCount - 1}
          onClick={() => onOffsetChange(Math.min((pageCount - 1) * safeLimit, (page + 1) * safeLimit))}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
